import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect } from 'react'
import { setAnalyticsUser } from '@/core/analytics'
import {
  identifyAnalyticsPlanFraming,
  noteAnalyticsPlanFraming,
} from '@/core/analytics/plan-framing'
import { graphqlClient } from '@/core/api/graphql-client'
import { changeLanguage, getCurrentLanguage, localeFromUrl } from '@/core/i18n'
import {
  appToBackendLanguage,
  backendToAppLanguage,
  type LanguageCode,
} from '@/core/i18n/languages'
import { useAuthStore } from '@/features/auth/store'
import {
  DayDocument,
  DetailedSegmentDocument,
  type Locale,
  MyPlanDocument,
  PlanUserProgressDocument,
  SetActivePlanDocument,
  SetLanguageDocument,
  UserDocument,
  UserProgressDataDocument,
} from '@/generated/graphql'
import { planFraming } from '../lib/plan-framing'

/**
 * Step 1: Fetch user data to get activePlanId.
 * Mirrors iOS: UserDataStore.loadUser()
 */
export function useUser() {
  // Gate on a *validated* session: `useUser` runs in ProtectedLayout above the
  // route gate, so an unauthenticated/stale-session mount would otherwise fire
  // `me` tokenless → backend "No auth token" (Sentry BACKEND-65).
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const query = useQuery({
    queryKey: ['user'],
    queryFn: () => graphqlClient.request(UserDocument),
    enabled: isAuthenticated,
  })

  // Identify against the resolved user rather than only at sign-in. Amplitude
  // persists its own identity, but any session that did not come through the
  // store's login — a restored blob, an injected one — would otherwise emit
  // anonymously for the whole page load. Idempotent, so repeat mounts are free.
  const userId = query.data?.me.id
  useEffect(() => {
    if (userId) setAnalyticsUser(userId)
  }, [userId])

  // Keyed on the arm, not the user: a plan switch changes it under one user id.
  const framing = query.data ? planFraming(query.data.me) : null
  if (framing) noteAnalyticsPlanFraming(framing)
  useEffect(() => {
    if (framing) identifyAnalyticsPlanFraming(framing)
  }, [framing])

  useEffect(() => {
    const lang = query.data?.me.language
    if (!lang) return

    const appCode = backendToAppLanguage(lang)
    if (localeFromUrl) {
      // URL locale overrides — sync to backend if different
      const currentLang = appToBackendLanguage(
        getCurrentLanguage() as LanguageCode,
      )
      if (currentLang !== lang) {
        graphqlClient
          .request(SetLanguageDocument, { language: currentLang as Locale })
          .catch(() => {})
      }
    } else if (appCode !== getCurrentLanguage()) {
      changeLanguage(appCode)
    }
  }, [query.data?.me.language])

  return query
}

/**
 * Step 2: Fetch plan with all segments (weeks).
 * Mirrors iOS: PlanDataStore.loadPlan(id:)
 * Cache policy: networkFirst (staleTime handles this)
 */
export function usePlan(planId: string | undefined | null) {
  return useQuery({
    queryKey: ['plan', planId],
    queryFn: () => graphqlClient.request(MyPlanDocument, { planId: planId! }),
    enabled: !!planId,
  })
}

/**
 * Plan-level user progress — `currentSegmentId` (BE-computed "what's next")
 * plus `isCompleted`.
 *
 * Note: `currentDayId` is intentionally NOT selected here. Selecting it
 * surfaced a "Property 'day' was not found in 'UserDayEntity'" error, so
 * we derive the current day FE-side from segment day completion instead
 * (see plan.tsx). Re-enable once that resolver is confirmed working.
 */
export function usePlanUserProgress(planId: string | undefined | null) {
  return useQuery({
    queryKey: ['plan-user-progress', planId],
    queryFn: () =>
      graphqlClient.request(PlanUserProgressDocument, { planId: planId! }),
    enabled: !!planId,
  })
}

/**
 * Step 3: Fetch detailed segment (days in week).
 * Mirrors iOS: PlanDataStore.loadDetailed(segment:)
 * Cache policy: networkFirst
 */
export function useDetailedSegment(segmentId: string | undefined | null) {
  return useQuery({
    queryKey: ['segment', segmentId],
    queryFn: () =>
      graphqlClient.request(DetailedSegmentDocument, {
        segmentId: segmentId!,
      }),
    enabled: !!segmentId,
  })
}

/**
 * Fetch full day content with blocks (lazy).
 * Mirrors iOS: SegmentDayDataStore.loadDayContent(dayID:)
 * Only loaded when user opens a specific day.
 */
export function useDay(dayId: string | undefined | null) {
  return useQuery({
    queryKey: ['day', dayId],
    queryFn: () => graphqlClient.request(DayDocument, { dayId: dayId! }),
    enabled: !!dayId,
  })
}

/**
 * Fetch plan progress — completedDays / totalDays.
 * Mirrors iOS: UserProgressManager.fetchProgress()
 * Always fresh (staleTime: 0). After CompleteDay, optimistically updated.
 *
 * Note: this query intentionally does NOT include plan-level `userProgress`
 * (currentSegmentId/currentDayId). They live in their own minimal query
 * (PlanUserProgress) — see `usePlanUserProgress` for context.
 */
export function usePlanProgress(planId: string | undefined | null) {
  return useQuery({
    queryKey: ['progress', planId],
    queryFn: async () => {
      const data = await graphqlClient.request(UserProgressDataDocument, {
        planId: planId!,
      })
      const allDays = data.myPlan?.segments.flatMap((s) => s.days) ?? []
      const totalDays = allDays.length
      const completedDays = allDays.filter(
        (d) => d.userProgress?.isCompleted,
      ).length
      return { completedDays, totalDays }
    },
    enabled: !!planId,
    staleTime: 0,
  })
}

/**
 * Recover from a stale day/segment ID — usually caused by the user landing
 * on a `/plan?week=<id>` or `/plan/day/<id>` URL that no longer matches their
 * active plan (e.g., plan switched on another device, content rebuild on BE).
 *
 * Refetches `/me` so we get the freshest `activePlanId`, drops the stale
 * per-id caches, and triggers refetch of plan + plan-user-progress.
 *
 * Callers should `navigate('/plan', { replace: true })` after awaiting this.
 */
export async function recoverActivePlan(
  queryClient: QueryClient,
): Promise<void> {
  // Drop stale per-id caches so consumers don't re-render with old data
  queryClient.removeQueries({ queryKey: ['segment'] })
  queryClient.removeQueries({ queryKey: ['day'] })

  // Refetch /me first so plan-derived queries pick up the fresh activePlanId
  await queryClient.invalidateQueries({ queryKey: ['user'] })

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['plan'] }),
    queryClient.invalidateQueries({ queryKey: ['plan-user-progress'] }),
    queryClient.invalidateQueries({ queryKey: ['progress'] }),
  ])
}

/**
 * Set active plan mutation.
 * Mirrors iOS: PlanDataStore.setActive(plan:) + ProfileViewModel.setActivePlan()
 * After mutation: invalidates user, plan, and segment queries.
 */
/**
 * Set active plan mutation.
 * Mirrors iOS: PlanDataStore.setActive(plan:) + ProfileViewModel.setActivePlan()
 *
 * Sequence (matches iOS):
 * 1. setActivePlan mutation
 * 2. Invalidate user query (refetches with new activePlanId)
 * 3. Invalidate plan + segment queries (refetched on plan page)
 * 4. Clear local progress (new plan = fresh start)
 */
/**
 * Set active plan mutation.
 * Mirrors iOS: PlanDataStore.setActive(plan:) + ProfileViewModel.setActivePlan()
 *
 * Sequence (1:1 with iOS):
 * 1. setActivePlan mutation
 * 2. loadPlan(id) — fetch new plan data
 * 3. loadUser() — refresh user with new activePlanId
 * 4. loadDetailed(firstSegment) — fetch first week of new plan
 */
export function useSetActivePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (planId: string) => {
      // Step 1: mutation
      await graphqlClient.request(SetActivePlanDocument, {
        planId,
      })

      // Drop the old plan's journey BEFORE the user cache updates — the
      // onboarding redirect fires off the user cache and must never read the
      // previous plan's goal.
      queryClient.removeQueries({ queryKey: ['journey'] })

      // Step 2: load new plan (iOS: planDataStore.loadPlan(id:))
      const planData = await graphqlClient.request(MyPlanDocument, { planId })
      queryClient.setQueryData(['plan', planId], planData)

      // Step 3: refresh user (iOS: userDataStore.loadUser())
      const userData = await graphqlClient.request(UserDocument)
      queryClient.setQueryData(['user'], userData)

      // Step 4: load first segment (iOS: loadDetailed(segment:))
      const firstSegment = planData.myPlan?.segments[0]
      if (firstSegment) {
        const segmentData = await graphqlClient.request(
          DetailedSegmentDocument,
          { segmentId: firstSegment.id },
        )
        queryClient.setQueryData(['segment', firstSegment.id], segmentData)
      }

      // Clear old cached data
      queryClient.removeQueries({
        queryKey: ['plan'],
        predicate: (query) => query.queryKey[1] !== planId,
      })
      queryClient.removeQueries({
        queryKey: ['segment'],
        predicate: (query) => query.queryKey[1] !== firstSegment?.id,
      })
      // Invalidate progress and discovery for new plan
      queryClient.invalidateQueries({ queryKey: ['progress'] })
      queryClient.invalidateQueries({ queryKey: ['discovery-items'] })

      // Return new plan info for language warning check
      return userData.me.availablePlans.find((p) => p.id === planId)
    },
  })
}
